import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { acceptInviteToken, signupFromInvite, validateInviteToken } from "../../api/api";
import { adoptAccount } from "../../lib/sessionActions";
import { getSessionToken } from "../../lib/sessionToken";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import ImageUpload from "../../widgets/ImageUpload";
import UsernameField from "../../widgets/UsernameField";
import CreatePasswordField from "../../widgets/CreatePasswordField";

/**
 * Invite-only account creation. Public open signup is not offered —
 * personal accounts come from email invitations; orgs use /new-org.
 *
 * @param {Object} props
 * @param {(user: Object) => void} props.onSetUser
 * @param {boolean} [props.inviteMode]
 * @returns {JSX.Element}
 */
function Signup({ onSetUser, inviteMode = false }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    pfpUrl: "",
    bio: "",
  });
  const [inviteState, setInviteState] = useState({
    loading: false,
    validated: false,
    error: "",
    token: "",
    invitedEmail: "",
    organizationName: "",
    accountExists: false,
  });
  const [submitError, setSubmitError] = useState("");
  const [usernameValid, setUsernameValid] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!inviteMode) {
      return;
    }
    const token = searchParams.get("token") || "";
    if (!token) {
      setInviteState((prev) => ({
        ...prev,
        validated: true,
        error: "Invitation token is missing.",
      }));
      return;
    }
    let cancelled = false;
    (async () => {
      setInviteState((prev) => ({ ...prev, loading: true, error: "", token }));
      const result = await validateInviteToken(token);
      if (cancelled) return;
      if (result.error || !result.success || !result.data) {
        setInviteState((prev) => ({
          ...prev,
          loading: false,
          validated: true,
          error:
            result.error ||
            result.message ||
            "This invitation is invalid or expired.",
        }));
        return;
      }
      const inviteData = result.data;
      setInviteState((prev) => ({
        ...prev,
        loading: false,
        validated: true,
        invitedEmail: inviteData.email || "",
        organizationName: inviteData.organization_name || "organization",
        accountExists: Boolean(inviteData.account_exists),
      }));
      setFormData((prev) => ({ ...prev, email: inviteData.email || prev.email }));
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteMode, searchParams]);

  if (!inviteMode) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-primary px-4">
        <div className="w-full max-w-md space-y-6 rounded-xl border border-ui-border bg-secondary p-8 shadow-md">
          <h2 className="text-center text-3xl font-bold text-accent">
            Invitation only
          </h2>
          <p className="text-center text-sm text-text-secondary">
            Personal Assembly accounts are created by email invitation. If you
            received an invite, open the link in that message.
          </p>
          <div className="flex flex-col gap-3">
            <Button asChild>
              <Link to="/login">Log in</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/create-account">Create account</Link>
            </Button>
          </div>
        </div>
      </main>
    );
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!usernameValid || !passwordValid) {
      return;
    }
    const result = await signupFromInvite({
      token: inviteState.token,
      first_name: formData.firstName,
      last_name: formData.lastName,
      username: formData.username,
      password: formData.password,
      pfp_url: formData.pfpUrl,
      bio: formData.bio,
    });
    if (result.error) {
      setSubmitError(result.error);
      return;
    }
    const userData = result?.data?.user || result?.data;
    if (result.token && userData) {
      const adopted = await adoptAccount(
        { ...userData, type: userData.type || "user" },
        result.token
      );
      if (adopted.error) {
        setSubmitError(adopted.error);
        return;
      }
    }
    if (userData) {
      onSetUser({ ...userData, type: userData.type || "user" });
    }
    navigate("/feed");
  };

  const handleAcceptInvite = async () => {
    setSubmitError("");
    if (!getSessionToken()) {
      setSubmitError("Log in as this account, then open the invitation again to accept it.");
      return;
    }
    const result = await acceptInviteToken(inviteState.token);
    if (result.error) {
      setSubmitError(result.error);
      return;
    }
    const userData = result?.data?.user || result?.data;
    if (userData) {
      onSetUser(userData);
    }
    navigate("/feed");
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-primary px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg space-y-6 rounded-xl border border-ui-border bg-secondary p-8 shadow-md"
      >
        <h2 className="text-center text-3xl font-bold text-accent">Invitation</h2>
        <div className="text-center text-sm text-text-secondary">
          {inviteState.loading ? "Validating invite…" : null}
          {!inviteState.loading && inviteState.error ? (
            <p className="rounded-md border border-ui-border bg-ui-muted px-3 py-2 text-text-secondary">
              {inviteState.error}
            </p>
          ) : null}
          {!inviteState.loading && !inviteState.error ? (
            <>
              You are invited to join{" "}
              <span className="font-semibold text-text-primary">
                {inviteState.organizationName}
              </span>
              .
            </>
          ) : null}
        </div>

        {submitError ? (
          <p className="rounded-md border border-ui-border bg-ui-muted px-3 py-2 text-sm text-text-secondary">
            {submitError}
          </p>
        ) : null}

        {inviteState.validated &&
        !inviteState.error &&
        inviteState.accountExists ? (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-primary/40 px-4 py-3 text-sm text-text-secondary">
              Account found for{" "}
              <span className="font-semibold text-text-primary">
                {inviteState.invitedEmail}
              </span>
              .
            </div>
            <Button type="button" className="w-full" onClick={handleAcceptInvite}>
              Accept invitation
            </Button>
            <p className="text-center text-xs text-text-muted">
              Need a different account?{" "}
              <Link to="/login" className="text-accent underline">
                Log in
              </Link>
            </p>
          </div>
        ) : null}

        {inviteState.validated &&
        !inviteState.error &&
        !inviteState.accountExists ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-border bg-secondary px-4 py-2 text-text-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-border bg-secondary px-4 py-2 text-text-primary"
                />
              </div>
            </div>

            <UsernameField
              id="signup-username"
              value={formData.username}
              onChange={(username) => {
                setUsernameValid(false);
                setFormData((prev) => ({ ...prev, username }));
              }}
              onValidityChange={setUsernameValid}
              inputClassName="h-10 bg-secondary px-4 py-2"
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                value={inviteState.invitedEmail}
                readOnly
                className="mt-1 block w-full rounded-md border border-border bg-secondary px-4 py-2 text-text-primary"
              />
            </div>

            <CreatePasswordField
              id="signup-password"
              value={formData.password}
              onChange={(password) => setFormData((prev) => ({ ...prev, password }))}
              showConfirm
              confirmValue={confirmPassword}
              onConfirmChange={setConfirmPassword}
              onValidityChange={setPasswordValid}
              inputClassName="h-10 bg-secondary px-4 py-2"
            />

            <ImageUpload
              value={formData.pfpUrl}
              onChange={(imageData) => {
                setFormData({
                  ...formData,
                  pfpUrl: imageData.url || "",
                });
              }}
              label="Profile picture"
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">
                Bio
              </label>
              <Textarea
                name="bio"
                rows={3}
                value={formData.bio}
                onChange={handleChange}
                className="mt-1 bg-secondary px-4"
                placeholder="Tell us a bit about yourself…"
              />
            </div>

            <Button type="submit" className="w-full" disabled={!usernameValid || !passwordValid}>
              Create account and accept invitation
            </Button>
          </>
        ) : null}

        {inviteState.validated && inviteState.error ? (
          <div className="flex flex-col gap-3">
            <Button asChild>
              <Link to="/login">Log in</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        ) : null}
      </form>
    </main>
  );
}

export default Signup;
